// Dots and Boxes interface. Tap the gap between two dots to draw a line there. Complete a box and it's yours, and you
// draw again. The computer's lines from its last turn stay marked until your next line. Undo takes back your last line
// and everything the computer did after it. The 4 by 4 board is in the server HTML (games/boards/_dots_and_boxes.html.erb),
// so nothing moves on load; another size is built in the same square.
import { boxesOf, chooseLine, draw, hintLine, legalMoves, LEVELS, lineAt, lineName, newGame, score, sides, SIZES, type DotsState, type Level } from "./dots"
import { readJSON, writeJSON } from "../lib/store"
import { gameEvent } from "../lib/game-events"
import { gameProgress, newAttempt } from "../lib/stats"

const SAVE = "dt:save:dots-and-boxes:v1"
const RULESET = "dots-and-boxes"
interface Saved { v: 1; state: DotsState; history: DotsState[]; level: Level; size: number; game: number; last: number[] }

/** The board's cells, row by row: dots, lines (buttons) and boxes. Mirrors games/boards/_dots_and_boxes.html.erb. */
export function boardHTML(size: number): string {
  const s = { rows: size, cols: size }
  const h = (size + 1) * size
  let out = ""
  for (let gr = 0; gr <= 2 * size; gr++) {
    for (let gc = 0; gc <= 2 * size; gc++) {
      if (gr % 2 === 0 && gc % 2 === 0) out += `<span class="db-dot" aria-hidden="true"></span>`
      else if (gr % 2 === 0) { const l = (gr / 2) * size + (gc - 1) / 2; out += `<button type="button" class="db-line is-h" data-line="${l}" tabindex="-1" aria-label="Line ${lineName(s, l)}"><span class="db-ink"></span></button>` }
      else if (gc % 2 === 0) { const l = h + ((gr - 1) / 2) * (size + 1) + gc / 2; out += `<button type="button" class="db-line is-v" data-line="${l}" tabindex="-1" aria-label="Line ${lineName(s, l)}"><span class="db-ink"></span></button>` }
      else out += `<span class="db-box" data-box="${((gr - 1) / 2) * size + (gc - 1) / 2}" aria-hidden="true"></span>`
    }
  }
  return out
}

export class DotsGame {
  private s: DotsState = newGame()
  private history: DotsState[] = []
  private level: Level = "medium"
  private size = 4 // the size for the next new game
  private game = 0
  private last: number[] = [] // the computer's lines from its last turn
  private hint: number | null = null
  private thinking = false
  private sentFirst = false
  private hints = 0
  private undos = 0
  private focus = 0
  private readonly status = document.querySelector<HTMLElement>("[data-status]")
  private readonly grid: HTMLElement

  constructor(private readonly root: HTMLElement) {
    this.grid = root.querySelector<HTMLElement>("[data-db-grid]")!
    const saved = readJSON<Saved>(SAVE)
    if (saved?.v === 1 && SIZES.includes(saved.state?.rows as 3) && saved.state.lines?.length) {
      this.s = saved.state; this.history = saved.history ?? []; this.level = LEVELS.includes(saved.level) ? saved.level : "medium"
      this.size = SIZES.includes(saved.size as 3) ? saved.size : this.s.rows
      this.game = saved.game; this.last = saved.last ?? []
      if (!this.s.over) this.say("Welcome back. Your game is where you left it.")
    } else {
      this.game = newAttempt()
    }
    this.build()
    const level = document.querySelector<HTMLSelectElement>("select[name=db-level]")
    if (level) {
      level.value = this.level
      level.addEventListener("change", () => {
        this.level = level.value as Level
        this.persist()
        this.say(`Computer: ${level.selectedOptions[0]?.textContent ?? this.level}.`)
      })
    }
    const size = document.querySelector<HTMLSelectElement>("select[name=db-size]")
    if (size) {
      size.value = String(this.size)
      size.addEventListener("change", () => {
        this.size = Number(size.value)
        if (this.s.moves === 0 || this.s.over) return this.newGame()
        this.persist()
        this.say(`Your next game will be ${this.size} by ${this.size} boxes. Choose New game to start it now.`)
      })
    }
    this.grid.addEventListener("click", (e) => {
      const b = (e.target as HTMLElement).closest<HTMLElement>("[data-line]")
      if (b) this.tap(Number(b.dataset.line))
    })
    this.grid.addEventListener("keydown", (e) => this.onKey(e))
    document.querySelector("[data-toolbar]")?.addEventListener("click", (e) => {
      const act = (e.target as HTMLElement).closest<HTMLElement>("[data-act]")?.dataset.act
      if (act === "undo") this.undo()
      else if (act === "hint") this.showHint()
      else if (act === "new") this.newGame()
    })
    document.addEventListener("keydown", (e) => {
      if ((e.target as HTMLElement).closest?.("input, select, textarea")) return
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "z") { e.preventDefault(); this.undo() }
      else if (!e.ctrlKey && !e.metaKey && !e.altKey && e.key.toLowerCase() === "h") this.showHint()
    })
    this.render()
    if (this.s.turn === "cpu" && !this.s.over) this.computerTurn()
  }

  private get pace(): number { return document.documentElement.dataset.motion === "off" ? 150 : 550 }

  /** Builds the grid for the current game's size, when it isn't the 4 by 4 one already on the page. */
  private build(): void {
    if (Number(this.grid.dataset.size) !== this.s.rows) {
      this.grid.innerHTML = boardHTML(this.s.rows)
      this.grid.dataset.size = String(this.s.rows)
      this.grid.style.setProperty("--n", String(this.s.rows))
    }
    if (!this.grid.querySelector(`[data-line="${this.focus}"]`)) this.focus = 0
  }

  private persist(): void {
    writeJSON(SAVE, { v: 1, state: this.s, history: this.history.slice(-120), level: this.level, size: this.size, game: this.game, last: this.last } satisfies Saved)
  }

  private tap(l: number): void {
    this.focus = l
    if (this.thinking) return this.say("The computer is thinking.")
    if (this.s.over) return this.say("This game is over. Choose New game to play again.")
    if (this.s.turn !== "you") return
    if (this.s.lines[l]) return this.say("That line is already drawn. Choose a gap between two dots.")
    this.history.push(this.s)
    const [next, made] = draw(this.s, l)
    this.s = next
    this.hint = null; this.last = []
    if (!this.sentFirst) { this.sentFirst = true; gameEvent({ type: "first_move", ruleset: RULESET, daily: false }) }
    this.persist()
    this.render()
    if (this.s.over) return this.finish(made ? `You completed ${boxes(made)}.` : "You drew the last line.")
    if (made) return this.say(`You completed ${boxes(made)}! Draw another line.`)
    this.say("The computer is thinking…")
    this.computerTurn()
  }

  /** The computer draws lines one at a time, a short pause apart, until it draws one that completes no box. */
  private computerTurn(drawn = 0, made = 0): void {
    this.thinking = true
    this.render()
    setTimeout(() => {
      const before = this.s
      if (before.turn !== "cpu" || before.over) return
      const l = chooseLine(before, this.level)
      if (this.s !== before) return // New game or Undo meanwhile
      const [next, got] = draw(before, l)
      this.s = next
      this.last = [...this.last, l]
      drawn++; made += got
      this.persist()
      if (!this.s.over && this.s.turn === "cpu") {
        this.render()
        this.say(`The computer completed ${boxes(made)} and draws again…`)
        return this.computerTurn(drawn, made)
      }
      this.thinking = false
      this.render()
      const what = made ? `The computer drew ${drawn} ${drawn === 1 ? "line" : "lines"} and completed ${boxes(made)}.` : "The computer drew a line."
      if (this.s.over) return this.finish(what)
      this.say(`${what} Your turn. Its lines are marked in yellow.`)
    }, this.pace)
  }

  private finish(what: string): void {
    const o = this.s.over!
    this.thinking = false
    gameEvent({ type: "end", ruleset: RULESET, outcome: o.winner === "you" ? "win" : o.winner === "draw" ? "abandon" : "loss", moves: this.s.moves, hints: this.hints, undos: this.undos, daily: false })
    this.render()
    this.say(`${what} ${o.winner === "you" ? `You won, ${o.you} boxes to ${o.cpu}!` : o.winner === "draw" ? `It's a draw, ${o.you} boxes each.` : `The computer won, ${o.cpu} boxes to ${o.you}. Undo to try again, or start a new game.`}`)
  }

  private undo(): void {
    if (this.thinking) return this.say("Wait for the computer's move, then Undo.")
    const prev = this.history.pop()
    if (!prev) return this.say("Nothing to undo.")
    this.undos++
    this.s = prev
    this.hint = null; this.last = []
    this.persist()
    this.render()
    this.say("Your last line and the computer's reply are taken back. Your turn.")
  }

  private showHint(): void {
    if (this.thinking || this.s.over || this.s.turn !== "you") return
    const l = hintLine(this.s)
    this.hints++
    this.hint = l
    this.render()
    const gives = boxesOf(this.s, l).some((b) => this.s.boxes[b] === null && boxSidesDrawn(this.s, b) === 3)
    this.say(`Hint: draw the line that's outlined${gives ? ". It completes a box" : ""}.`)
  }

  private newGame(): void {
    if (this.sentFirst && !this.s.over) gameEvent({ type: "end", ruleset: RULESET, outcome: "new_game", moves: this.s.moves, hints: this.hints, undos: this.undos })
    this.s = newGame(this.size)
    this.history = []
    this.game = newAttempt()
    this.hint = null; this.last = []; this.thinking = false
    this.sentFirst = false; this.hints = 0; this.undos = 0
    this.build()
    this.persist()
    this.render()
    this.say(`New game, ${this.size} by ${this.size} boxes. You draw first.`)
  }

  private onKey(e: KeyboardEvent): void {
    const b = (e.target as HTMLElement).closest<HTMLElement>("[data-line]")
    if (!b) return
    const l = Number(b.dataset.line)
    // Left and Right move along the row of the grid you're in; Up and Down move one row and take the nearest line in it.
    const dr = e.key === "ArrowUp" ? -1 : e.key === "ArrowDown" ? 1 : 0, dc = e.key === "ArrowLeft" ? -1 : e.key === "ArrowRight" ? 1 : 0
    if (dr || dc) {
      e.preventDefault()
      const [gr, gc] = this.cell(l)
      let best = -1, bestD = Infinity
      for (let m = 0; m < this.s.lines.length; m++) {
        const [r, c] = this.cell(m)
        const d = dr ? (r === gr + dr ? Math.abs(c - gc) * 2 + (c > gc ? 1 : 0) : Infinity) : r === gr && (c - gc) * dc > 0 ? Math.abs(c - gc) : Infinity
        if (d < bestD) { bestD = d; best = m }
      }
      if (best >= 0) { this.focus = best; this.render(); this.grid.querySelector<HTMLElement>(`[data-line="${best}"]`)?.focus() }
      return
    }
    if (e.key === "Enter" || e.key === " ") { e.preventDefault(); this.tap(l); this.grid.querySelector<HTMLElement>(`[data-line="${this.focus}"]`)?.focus() }
  }

  /** A line's place in the grid of dots, lines and boxes. */
  private cell(l: number): [number, number] {
    const { horizontal, r, c } = lineAt(this.s, l)
    return horizontal ? [2 * r, 2 * c + 1] : [2 * r + 1, 2 * c]
  }

  private render(): void {
    gameProgress({ key: SAVE, ruleset: RULESET, id: String(this.game), moves: this.s.moves, started: this.s.moves > 0, won: this.s.over?.winner === "you" })
    const open = new Set(this.s.turn === "you" && !this.thinking ? legalMoves(this.s) : [])
    const last = new Set(this.last)
    this.grid.querySelectorAll<HTMLElement>("[data-line]").forEach((b) => {
      const l = Number(b.dataset.line)
      const who = this.s.lines[l]
      b.classList.toggle("is-you", who === "you")
      b.classList.toggle("is-cpu", who === "cpu")
      b.classList.toggle("is-open", open.has(l))
      b.classList.toggle("is-last", last.has(l))
      b.classList.toggle("is-hint", l === this.hint)
      b.tabIndex = l === this.focus ? 0 : -1
      b.setAttribute("aria-label", `Line ${lineName(this.s, l)}: ${who ? `drawn by ${who === "you" ? "you" : "the computer"}${last.has(l) ? " just now" : ""}` : "not drawn"}`)
    })
    this.grid.querySelectorAll<HTMLElement>("[data-box]").forEach((el) => {
      const who = this.s.boxes[Number(el.dataset.box)]
      el.classList.toggle("is-you", who === "you")
      el.classList.toggle("is-cpu", who === "cpu")
      el.textContent = who === "you" ? "You" : who === "cpu" ? "PC" : ""
    })
    const count = this.root.querySelector<HTMLElement>("[data-db-count]")
    if (count) count.textContent = `Your boxes: ${score(this.s, "you")} · Computer's: ${score(this.s, "cpu")}`
    this.root.classList.toggle("is-thinking", this.thinking)
  }

  private say(msg: string): void { if (this.status) this.status.textContent = msg }
}

const boxes = (n: number): string => (n === 1 ? "a box" : `${n} boxes`)
const boxSidesDrawn = (s: DotsState, b: number): number => sides(s, b).filter((l) => s.lines[l]).length
